import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { fetchPostedProducts } from "../../services/InteractionAndOffer";
import { Range } from "react-range";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import Spinner from "../shared/Spinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const generateColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 40) + 60; // 60–100%
  const lightness = Math.floor(Math.random() * 30) + 10;  // 10–40% (dark)

  return hslToHex(hue, saturation, lightness);
};

const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;

  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))))
      .toString(16)
      .padStart(2, "0");

  return `#${f(0)}${f(8)}${f(4)}`;
};

function MonthlyTrend() {
  const [buyData, setBuyData] = useState({});
  const [sellData, setSellData] = useState({});
  const [totalData, setTotalData] = useState({});
  const [dates, setDates] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("Total");
  const [colors, setColors] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("Month"); // State for dropdown selection
  const [range, setRange] = useState([0, 0]);
  const [isExpanded, setIsExpanded] = useState(false); // Track if tags are expanded

  useEffect(() => {
    async function getData() {
      try {
        const data = await fetchPostedProducts();
        const buyOffersByDate = {};
        const sellOffersByDate = {};
        const totalOffersByDate = {};
        const productColors = {};
        const allDates = new Set();

        Object.keys(data).forEach((category) => {
          ["buy", "sell"].forEach((offerType) => {
            const offers = data[category]?.[offerType];

            if (offers) {
              offers.forEach((offer) => {
                const offerDate = new Date(offer.createdAt);
                const dateString =
                  viewType === "Month"
                    ? `${offerDate.toLocaleString("default", {
                        month: "long",
                      })} ${offerDate.getFullYear()}`
                    : offerDate.toISOString().split("T")[0];

                const productName = offer.product_name;

                allDates.add(dateString);

                if (!buyOffersByDate[productName]) {
                  buyOffersByDate[productName] = {};
                  sellOffersByDate[productName] = {};
                  totalOffersByDate[productName] = {};
                  productColors[productName] = generateColor();
                }

                if (offerType === "buy") {
                  buyOffersByDate[productName][dateString] =
                    (buyOffersByDate[productName][dateString] || 0) + 1;
                } else if (offerType === "sell") {
                  sellOffersByDate[productName][dateString] =
                    (sellOffersByDate[productName][dateString] || 0) + 1;
                }

                totalOffersByDate[productName][dateString] =
                  (totalOffersByDate[productName][dateString] || 0) + 1;
              });
            }
          });
        });

        const sortedDates = Array.from(allDates).sort(
          (a, b) => new Date(a) - new Date(b)
        );

        buyOffersByDate["Total"] = {};
        sellOffersByDate["Total"] = {};
        totalOffersByDate["Total"] = {};

        sortedDates.forEach((date) => {
          buyOffersByDate["Total"][date] = Object.keys(buyOffersByDate)
            .filter((key) => key !== "Total")
            .reduce(
              (sum, productName) =>
                sum + (buyOffersByDate[productName][date] || 0),
              0
            );

          sellOffersByDate["Total"][date] = Object.keys(sellOffersByDate)
            .filter((key) => key !== "Total")
            .reduce(
              (sum, productName) =>
                sum + (sellOffersByDate[productName][date] || 0),
              0
            );

          totalOffersByDate["Total"][date] =
            buyOffersByDate["Total"][date] + sellOffersByDate["Total"][date];
        });

        productColors["Total"] = "#0000FF";

        setBuyData(buyOffersByDate);
        setSellData(sellOffersByDate);
        setTotalData(totalOffersByDate);
        setDates(sortedDates);
        setColors(productColors);
        setRange([0, sortedDates.length - 1]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    getData();
  }, [viewType]);

  const handleProductClick = (productName) => {
    setSelectedProduct(productName);
  };

  const handleRangeChange = (values) => {
    setRange(values);
  };

  const filteredDates = dates.slice(range[0], range[1] + 1);
  // const chartData = {
  //   labels: filteredDates,
  //   datasets: [
  //     {
  //       label: "Buy Offers",
  //       data: filteredDates.map(
  //         (date) => buyData[selectedProduct]?.[date] || 0
  //       ),
  //       borderColor: viewType === "Month" ? "green" : "rgba(0,0,0,0)",
  //       backgroundColor:
  //         viewType === "Month" ? "rgba(0, 128, 0, 0.2)" : "rgba(0,0,0,0)",
  //       borderWidth: 2,
  //     },
  //     {
  //       label: "Sell Offers",
  //       data: filteredDates.map(
  //         (date) => sellData[selectedProduct]?.[date] || 0
  //       ),
  //       borderColor: viewType === "Month" ? "red" : "rgba(0,0,0,0)",
  //       backgroundColor:
  //         viewType === "Month" ? "rgba(255, 0, 0, 0.2)" : "rgba(0,0,0,0)",
  //       borderWidth: 2,
  //     },
  //     {
  //       label: "Total Offers",
  //       data: filteredDates.map(
  //         (date) => totalData[selectedProduct]?.[date] || 0
  //       ),
  //       borderColor: viewType === "Month" ? "blue" : "rgba(0,0,0,0)",
  //       backgroundColor:
  //         viewType === "Month" ? "rgba(0, 0, 255, 0.2)" : "rgba(0,0,0,0)",
  //       borderWidth: 2,
  //     },
  //   ],
  // };
  const chartData = {
    labels: filteredDates,
    datasets: [
      {
        label: "Buy Offers",
        data: filteredDates.map(
          (date) => buyData[selectedProduct]?.[date] || 0
        ),
        borderColor: "green",
        backgroundColor: "rgba(0, 128, 0, 0.2)",
        borderWidth: 2,
        tension: 0.4, // Add this line to curve the line
        hidden:
          viewType === "Day" &&
          !filteredDates.some((date) => buyData[selectedProduct]?.[date]),
      },
      {
        label: "Sell Offers",
        data: filteredDates.map(
          (date) => sellData[selectedProduct]?.[date] || 0
        ),
        borderColor: "red",
        backgroundColor: "rgba(255, 0, 0, 0.2)",
        borderWidth: 2,
        tension: 0.4, // Add this line to curve the line
        hidden:
          viewType === "Day" &&
          !filteredDates.some((date) => sellData[selectedProduct]?.[date]),
      },
      {
        label: "Total Offers",
        data: filteredDates.map(
          (date) => totalData[selectedProduct]?.[date] || 0
        ),
        borderColor: "blue",
        backgroundColor: "rgba(0, 0, 255, 0.2)",
        borderWidth: 2,
        tension: 0.4, // Add this line to curve the line
        hidden:
          viewType === "Day" &&
          !filteredDates.some((date) => totalData[selectedProduct]?.[date]),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: viewType === "Month" ? "Months" : "Days",
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Offers",
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      datalabels:
        viewType === "Month"
          ? {
              anchor: "end",
              align: "end",
              formatter: (value) => Math.round(value),
              color: "#000",
              font: {
                weight: "bold",
              },
            }
          : null,
    },
  };

  if (loading) {
    return <Spinner size="lg" centered={true} />;
  }

  return (
    <div>
      <h2>
        {selectedProduct === "Total"
          ? "Total Offers Trend"
          : `${selectedProduct} Offers Trend`}
      </h2>

      {/* Dropdown to select Month or Day view */}
      <div style={{ marginBottom: "10px" }}>
        <label htmlFor="viewType" style={{ marginRight: "10px" }}>
          View by:
        </label>
        <select
          id="viewType"
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
          style={{
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        >
          <option value="Month">Monthly View</option>
          <option value="Day">Daily View</option>
        </select>
      </div>

      {/* Collapsible Product Tags */}
      <div style={{ marginBottom: "10px" }}>
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Header - Always Visible */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 15px",
              backgroundColor: isExpanded ? "#f3f4f6" : "#ffffff",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!isExpanded) {
                e.currentTarget.style.backgroundColor = "#f9fafb";
              }
            }}
            onMouseLeave={(e) => {
              if (!isExpanded) {
                e.currentTarget.style.backgroundColor = "#ffffff";
              }
            }}
          >
            <span style={{ fontWeight: "500", color: "#374151" }}>
              Product Tags ({Object.keys(buyData).length} products)
            </span>
            {isExpanded ? (
              <HiChevronUp style={{ fontSize: "20px", color: "#6b7280" }} />
            ) : (
              <HiChevronDown style={{ fontSize: "20px", color: "#6b7280" }} />
            )}
          </button>

          {/* Product Tags - Shows 6 when collapsed, all when expanded */}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#ffffff",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {(() => {
              const productNames = Object.keys(buyData);
              const productsToShow = isExpanded 
                ? productNames 
                : productNames.slice(0, 6);
              
              return productsToShow.map((productName) => (
                <button
                  key={productName}
                  onClick={() => handleProductClick(productName)}
                  style={{
                    backgroundColor:
                      selectedProduct === productName
                        ? colors[productName]
                        : "transparent",
                    color:
                      selectedProduct === productName ? "white" : "black",
                    padding: "8px 14px",
                    border: `1px solid ${colors[productName]}`,
                    cursor: "pointer",
                    borderRadius: "6px",
                    transition: "all 0.2s",
                    fontWeight: selectedProduct === productName ? "600" : "400",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedProduct !== productName) {
                      e.currentTarget.style.backgroundColor = `${colors[productName]}20`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedProduct !== productName) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      backgroundColor: colors[productName],
                      marginRight: "8px",
                      borderRadius: "50%",
                    }}
                  ></span>
                  {productName}
                </button>
              ));
            })()}
          </div>
        </div>
      </div>

      <div style={{ height: "300px", position: "relative" }}>
        <Line data={chartData} options={options} />
      </div>

      {dates.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <Range
            values={range}
            step={1}
            min={0}
            max={dates.length - 1}
            onChange={handleRangeChange}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "8px",
                  width: "100%",
                  backgroundColor: "#ddd",
                  borderRadius: "8px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    height: "100%",
                    backgroundColor: "#4A90E2",
                    borderRadius: "8px",
                    left: `${(range[0] / (dates.length - 1)) * 100}%`,
                    width: `${
                      ((range[1] - range[0]) / (dates.length - 1)) * 100
                    }%`,
                  }}
                />
                {children}
              </div>
            )}
            renderThumb={({ props, index }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "20px",
                  width: "20px",
                  backgroundColor: "#4A90E2",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-28px",
                    color: "#4A90E2",
                    fontSize: "12px",
                  }}
                >
                  {dates[range[index]]}
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}

export default MonthlyTrend;
